import { useCallback, useRef, useState } from 'react';
import { useLocalStorageState } from 'ahooks';

import { useUser, isUserLoggedIn } from '~/modules/user';
import { useAuth } from '~/modules/root/context/AuthContext';

/** Sends the requestDownloadAccess mutation via plain fetch (no Apollo needed). */
const requestDownloadAccessMutation = (
    professionalID: number,
    libraryItemID: number,
): Promise<void> =>
    fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            query: `mutation RequestDownloadAccess($ProfessionalID: Int!, $LibraryItemID: Int) {
                requestDownloadAccess(ProfessionalID: $ProfessionalID, LibraryItemID: $LibraryItemID) {
                    ... on Contact { ID }
                }
            }`,
            variables: {
                ProfessionalID: professionalID,
                LibraryItemID: libraryItemID,
            },
        }),
    }).then(() => undefined);

interface UseGatedDownloadsOptions {
    /** The product page ID. */
    productID: number;
    /** The professional's ID. */
    professionalID: number;
    /** Called before the auth modal opens (e.g. close a hosting modal). */
    onBeforeAuth?: () => void;
    /** Called when the user cancels the auth modal. */
    onAuthCancel?: () => void;
}

/**
 * Per-file download gating shared by the product-files modal and the revamp's
 * inline Downloads & Documentation accordion row:
 * - Safety: always free
 * - Specs: downloading creates a Lead (once per session, persisted per
 *   professional in localStorage) and shares the user's details
 * - ProDesign: requires sign-in
 */
export const useGatedDownloads = ({
    productID,
    professionalID,
    onBeforeAuth,
    onAuthCancel,
}: UseGatedDownloadsOptions) => {
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { showAuthModal, hideAuthModal } = useAuth();

    const storageKey = `specsAccess_${professionalID}`;
    const [hasSpecsAccess, setHasSpecsAccess] = useLocalStorageState<boolean>(
        storageKey,
        { defaultValue: false },
    );

    const hasCreatedLead = useRef(false);
    const [leadConfirmed, setLeadConfirmed] = useState(false);

    /** Whether Specs-tier files should be gated (not logged in, or no prior access). */
    const specsGated = !isLoggedIn || !hasSpecsAccess;

    /** Creates a lead on the first gated download, then persists access for future visits. */
    const createLeadIfNeeded = useCallback(() => {
        if (hasCreatedLead.current) return;
        hasCreatedLead.current = true;
        setHasSpecsAccess(true);
        setLeadConfirmed(true);

        requestDownloadAccessMutation(professionalID, productID).catch(
            () => undefined,
        );
    }, [professionalID, productID, setHasSpecsAccess]);

    /**
     * Handles a gated file download. Shows the auth modal if needed,
     * then opens the file and creates a lead for Specs-tier files.
     */
    const handleGatedDownload = useCallback(
        (link: string, isSpecsTier: boolean) => {
            const doDownload = () => {
                window.open(link, '_blank', 'noopener');
                if (isSpecsTier) {
                    createLeadIfNeeded();
                }
            };

            if (!isLoggedIn) {
                onBeforeAuth?.();
                showAuthModal({
                    authSource: 'productFiles',
                    skipPostSignUp: true,
                    redirect: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                    header: 'Sign in to download',
                    subtitle:
                        'Create a free account to download product files and resources.',
                    onAuthSuccess: () => {
                        hideAuthModal();
                    },
                    onCancel: () => {
                        hideAuthModal();
                        onAuthCancel?.();
                    },
                });
                return;
            }

            doDownload();
        },
        [
            isLoggedIn,
            showAuthModal,
            hideAuthModal,
            createLeadIfNeeded,
            onBeforeAuth,
            onAuthCancel,
        ],
    );

    return { isLoggedIn, specsGated, leadConfirmed, handleGatedDownload };
};
